import Backbone from 'backbone';
import $ from 'jquery';
import {debounce} from 'ts-debounce';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import * as BenchlingUtil from 'BenchlingUtil';
import {SequenceStatusBarState, StatusBarState} from 'components/StatusBar';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {FilePreferences} from 'folder-items/FileConstants';
import navigateToURL, {NavigateToURLOptions} from 'lib/navigateToURL';
import SidepanelStore from 'nav/sidepanel/SidepanelStore';
import * as navigation from 'Navigation';
import {BufferState, BufferStates, TabState, TabStates} from 'Navigation';
import preferences from 'Preferences';
import {SchemaValidationState} from 'schemas/SchemaValidationConstants';
import {Optional} from 'util/Types';

export interface FileStateAttributes {
  toolbar: Backbone.Model | null;
  statusbar: StatusBarState;
  active: boolean;
  name: string;
  id: string | null;
  hideTabNav: boolean;
}

class FileState extends Backbone.Model {
  splitState?: {[tabCid: string]: {active: boolean; buffer: BufferState}};

  override defaults(): FileStateAttributes {
    return {
      toolbar: null,
      statusbar: new SequenceStatusBarState(),
      active: false,
      name: '', // the name that will show up in the tab
      id: null, // required as it gets bound as a data attribute on the element
      hideTabNav: false,
    };
  }

  constructor(attributes?: object, options?: unknown) {
    // oxlint-disable-next-line prefer-rest-params
    super(...arguments);
    // Avoid re-syncing if there are multiple edits happening
    const debouncedSyncPreference = debounce(this.saveLayoutPreference.bind(this), 1);
    this.listenTo(this.buffers(), 'reorderTabs change:active', debouncedSyncPreference);
    this.buffers().forEach((buffer) => {
      this.listenTo(buffer.tabs(), 'add remove reset change:active', debouncedSyncPreference);
    });
  }

  override initialize(attributes?: object, options?: unknown): void {
    this.registerTitleAndURLSync();
  }

  /**
   * Register listeners to call `this.syncTitleAndURL` on `editURL` / `name` change.
   */
  registerTitleAndURLSync(): void {
    throw new Error('registerTitleAndURLSync not implemented');
  }

  buffers(): BufferStates {
    return this.get('buffers');
  }
  active(): boolean {
    return this.get('active');
  }
  name(): Optional<string> {
    return this.get('name');
  }
  archiveRecord(): ArchiveRecord | null {
    return null;
  }
  setArchiveRecord(archiveRecord: ArchiveRecord | null): void {
    throw new Error('setArchiveRecord not implemented.');
  }
  permissions(): Permissions | null {
    return null;
  }
  setPermissions(permissions: Permissions): void {
    throw new Error('setPermissions not implemented.');
  }
  schemaValidationState(): Optional<SchemaValidationState> {
    return null;
  }
  fileType(): ModelTypeConstants {
    // Concrete subclasses expected to declare fileType like:
    // ClassFileState.fileType = ModelTypeConstants.NAME
    // @ts-expect-error
    return this.constructor.fileType;
  }
  isFolderItem(): boolean {
    return false;
  }
  hasUnsavedChanges(): boolean {
    return false;
  }
  handleSplitWorkspaceChange(): void {}

  editURL(): string | undefined {
    return this.get(this.fileType()).editURL();
  }

  syncTitleAndURL(titleAndUrl?: {name?: string; url?: string} | null, options?: NavigateToURLOptions): void {
    if (titleAndUrl == null) {
      titleAndUrl = {};
    }
    let {name, url}: {name?: Optional<string>; url?: string | null} = titleAndUrl;
    if (options == null) {
      options = {};
    }
    let {replace} = options;
    const {skipTrigger} = options;
    if (replace == null) {
      replace = true;
    }
    if (url == null) {
      url = this.editURL();
    }
    if (name == null) {
      name = this.name();
    }
    if (this.active() && !SidepanelStore.getIsExpanded()) {
      navigateToURL(url!, {replace, skipTrigger});
      BenchlingUtil.setWindowTitle(name as string | undefined);
    }
  }

  /**
   * Hook that may be overridden by subclasses to perform an operation before being activated.
   */
  beforeActivate(): void {}

  open(param?: {open?: boolean}): JQueryPromise<FileState> {
    if (param == null) {
      param = {};
    }
    let {open} = param;
    const FileStates = require('../FileStates').default; // The ultimate circular dependency
    if (open == null) {
      open = true;
    }
    const file = this;
    if (open) {
      FileStates.get().activate(file);
    }
    return $.Deferred(function () {
      // @ts-expect-error: `this` binds to the deferred object
      return this.resolve(file);
    }).promise() as JQueryPromise<FileState>;
  }

  findTab<T extends TabState>(
    tabStateClass: {new (...args: Array<unknown>): T},
    filter: null | ((tabState: T) => boolean) = null
  ): T | null {
    for (const buffer of Array.from(this.buffers().models)) {
      for (const tab of Array.from(buffer.tabs().models)) {
        if (tab instanceof tabStateClass && (filter == null || filter(tab))) {
          return tab;
        }
      }
    }
    return null;
  }

  activateTab(tabStateClass: typeof TabState): void {
    const tab = this.findTab(tabStateClass)!;
    tab.set({active: true});
  }

  getSplitWorkspacePreferenceKey(): string {
    const fileType = this.fileType();
    assert(fileType != null, 'fileType should be set on concrete FileState class');
    return `${FilePreferences.SPLIT_WORKSPACE_NAMESPACE}${fileType}`;
  }

  updateWorkspaceSplitPreference(isSplit: boolean): void {
    const splitWorkspacePreferenceKey = this.getSplitWorkspacePreferenceKey();
    // `handleSplitWorkspaceChange` is always called on open. Save an (unnecessary) XHR on open if the old and
    // new values are equal.
    if (preferences.get(splitWorkspacePreferenceKey) !== isSplit) {
      preferences.set(splitWorkspacePreferenceKey, isSplit);
    }
  }

  /**
   * Initialize split workspace from preference
   *
   * This applies the split workspace preference. But it applies the preference conditionally, since some
   * `FileState`s, like Storables, dynamically support 1 or 2 buffers.
   *
   * Over time, we should migrate all remaining `FileState.initialize` methods to call this
   * `initIsWorkspaceSplitFromPreference` method at the end of their body, instead of their own bespoke
   * implementation or omission.
   */
  initIsWorkspaceSplitFromPreference(): void {
    // We read from `StatusBarState`, because it should be the source-of-truth for:
    //   * How many buffers the `FileState` supports (`numBuffers`)
    //   * The default split workspace (`splitWorkspace`)
    const statusBarState = this.get('statusbar');
    if (statusBarState.get('numBuffers') < 2) {
      // Only 1 buffer, so we cannot split workspace, which requires 2 buffers
      return;
    }
    const defaultSplit = statusBarState.get('splitWorkspace');
    const splitPref = preferences.get(this.getSplitWorkspacePreferenceKey());
    this.setIsWorkspaceSplit(splitPref != null ? splitPref : defaultSplit);
  }

  setIsWorkspaceSplit(isSplit: boolean): void {
    this.get('statusbar').set('splitWorkspace', isSplit);
    this.layoutWorkspace();
  }

  layoutWorkspace(): void {
    let buffer;
    let oldBuffer;
    let tab;
    const isWorkspaceSplit = this.get('statusbar').isWorkspaceSplit();
    const numBuffers = this.get('statusbar').get('numBuffers');
    $('#editor').toggleClass('hasSplitWorkspace', isWorkspaceSplit || false);

    // Keep this logic idempotent: if the workspace UI already matches the isWorkspaceSplit, then
    // don't do anything
    if (isWorkspaceSplit && this.buffers().where({active: true}).length === 1) {
      this.splitState = this.splitState != null ? this.splitState : {};

      // Re-activate all (allowed) buffers
      for (buffer of Array.from(this.buffers().models.slice(1, numBuffers))) {
        buffer.set({active: true});
      }

      // When we split workspace, all tabs should currently be in the first buffer. We slice the models
      // since in the body of the for loop we will be adding/removing tabs to the first buffer.
      const firstBuffer = this.buffers().at(0);
      const activeTab = firstBuffer.tabs().findWhere({active: true});
      const bufferForActiveTab =
        activeTab != null
          ? (this.splitState[activeTab.cid] != null ? this.splitState[activeTab.cid].buffer : undefined) !=
            null
            ? this.splitState[activeTab.cid] != null
              ? this.splitState[activeTab.cid].buffer
              : undefined
            : firstBuffer
          : undefined;
      const allTabs = firstBuffer.tabs().models.slice();

      for (tab of Array.from(allTabs)) {
        // oxlint-disable-next-line no-var, vars-on-top
        var oldActive;
        const oldState = this.splitState[tab.cid];

        // If the tab wasn't present since the last call to unsplit, we leave it in the first buffer.
        if (oldState == null) {
          continue;
        }
        ({buffer: oldBuffer, active: oldActive} = oldState);

        // Move the tab to the correct buffer.
        if (oldBuffer !== firstBuffer) {
          // As we move tabs from the firstBuffer, some of the tabs (ie VirtualDigests) may also be removed.
          // If such a tab is removed, we do not want to force it back into a buffer as this may result
          // in duplicate tabs, e.g.:
          //  1. Buffer 1 has tabsToMoveToBuffer2=[digest, virtual digest], which we will iterate over.
          //  2. Iter 1: digest
          //       a. We remove digest from buffer 1, triggering VD to be removed from buffer 1 (0 digests)
          //       b. We add digest to buffer 2, so triggering a new VD to be added to buffer 2 (>0 digests)
          //  3. Iter 2: virtual digest
          //       a. We try to remove virtual digest from buffer 1 (it is already gone from 2a)
          //       b. We add virtual digest to buffer 2 (but there is already one there from 2b!)
          if (!firstBuffer.tabs().contains(tab)) {
            continue;
          }
          firstBuffer.tabs().remove(tab);
          oldBuffer.tabs().add(tab);
        }

        // Activate the tab if it was active and the currently active tab will not be put in the same buffer
        if (oldActive && oldBuffer !== bufferForActiveTab) {
          tab.set('active', true);
        }
      }

      // Make sure that for each buffer that has tabs, that one of its tabs is set to active.
      for (buffer of Array.from(this.buffers().models.slice(0, numBuffers))) {
        if (!buffer.tabs().isEmpty()) {
          if (!buffer.tabs().some((tab) => tab.get('active'))) {
            buffer.tabs().at(0).set('active', true);
          }
        }
      }

      // Keep this method idempotent by checking if there is more than one active buffer
    } else if (!isWorkspaceSplit && this.buffers().where({active: true}).length > 1) {
      this.splitState = {};
      for (buffer of Array.from(this.buffers().models)) {
        for (tab of Array.from(buffer.tabs().models)) {
          this.splitState[tab.cid] = {
            active: tab.get('active'),
            buffer,
          };
        }
      }
      buffer = this.buffers().at(0);
      for (oldBuffer of Array.from(this.buffers().models.slice(1, this.buffers().length))) {
        // First set old buffer to inactive so width of new buffer is correct when tabs are added
        oldBuffer.set('active', false);
        // Set all tabs on the right to inactive.
        // Then remove them from old buffer, and move them all to
        // the left buffer.
        const moveTabs = (() => {
          const result = [];
          for (tab of Array.from(oldBuffer.tabs().models)) {
            tab.set('active', false);
            result.push(tab);
          }
          return result;
        })();
        oldBuffer.tabs().reset();

        // `TabStates.add` was overridden to insert right of active tab by default to cover other use cases
        // Therefore, to handle combining buffers, we want to explicitly specify `at` to be the end of the tabs.
        // @ts-expect-error: Backbone permits adding by array to a collection: https://backbonejs.org/docs/backbone.html#section-114
        buffer.tabs().add(moveTabs, {at: buffer.tabs().length});
      }
    }

    // Trigger splitWorkspace hook
    this.handleSplitWorkspaceChange();
  }

  _tabListLayoutPreference(tabs: TabStates): Array<{id: string; active: boolean}> {
    return tabs
      .filter(
        (tab) =>
          !tab.get('closeable') &&
          tab.get('preferenceKey') &&
          tab.get('preferenceKey') !== navigation.PREFERENCE_KEY_IGNORE
      )
      .map((tab) => ({id: tab.get('preferenceKey'), active: tab.get('active')}));
  }

  saveLayoutPreference(): void {
    const filePrefKey = `${FilePreferences.WORK_SPACE_BUFFERS_NAMESPACE}${this.fileType()}`;
    const oldFilePrefValue = preferences.get(filePrefKey) || {};
    const bufferPrefValue = this.buffers().map((buffer) => this._tabListLayoutPreference(buffer.tabs()));
    const newFilePrevValue = {
      ...oldFilePrefValue,
      [this.buffers().getLayoutPreferenceKey()]: bufferPrefValue,
    };
    preferences.set(filePrefKey, newFilePrevValue);
  }
}
export default FileState;
